<script setup>
import { ref } from "vue";
import CopyActionButton from "./CopyActionButton.vue";

const instructionsCopied = ref(false);
const copyInstructions = () => {
    const lines = [
        `Beneficiary: ${props.wireDetails.accountName}`,
        `Bank: ${props.wireDetails.bankName}`,
        props.zelleReference ? `Reference: ${props.zelleReference}` : null,
        isSidebar && props.zelleReference ? `Amount: $${props.currencyFormat(props.totalAmount)}` : null,
        `Routing: ${props.wireDetails.routing}`,
        `Account: ${props.wireDetails.account}`,
        isSidebar && props.wireDetails.swiftCode ? `SWIFT: ${props.wireDetails.swiftCode}` : null,
        props.wireDetails.bankAddress ? `${isSidebar ? 'Bank Branch Address' : 'Bank Address'}: ${props.wireDetails.bankAddress}` : null,
        props.wireDetails.businessAddress ? `Business Address: ${props.wireDetails.businessAddress}` : null,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(lines);
    instructionsCopied.value = true;
    setTimeout(() => (instructionsCopied.value = false), 2000);
};

const props = defineProps({
    wireDetails: { type: Object, required: true },
    invoiceEmail: { type: String, default: "" },
    zelleReference: { type: String, default: "" },
    totalAmount: { type: [String, Number], default: "0" },
    selectedInvoicesCount: { type: Number, default: 0 },
    copiedKey: { type: String, default: "" },
    copyToClipboard: { type: Function, required: true },
    currencyFormat: { type: Function, required: true },
    layout: { type: String, default: "full" },
});

// Sidebar (invoice payments) shows extra rows; full layout (thank you page) keeps the original set
const isSidebar = props.layout === "sidebar";

const contentGridClass = isSidebar ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 md:grid-cols-2 gap-4";

const addressLabelClass = isSidebar ? "text-theme-body-light" : "text-theme-secondary";

const digitCount = (value) => String(value || "").replace(/\D/g, "").length;

const chunkDigits = (value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) return "";
    const groups = [];
    const remainder = digits.length % 3;
    if (remainder) groups.push(digits.slice(0, remainder));
    for (let index = remainder; index < digits.length; index += 3) {
        groups.push(digits.slice(index, index + 3));
    }
    return groups.join(" · ");
};
</script>

<template>
    <div class="space-y-2">
        <div
            class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-center justify-between gap-3">
            <div>
                <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Beneficiary</p>
                <p class="text-theme-secondary text-sm font-normal mt-0.5">{{ props.wireDetails.accountName }}</p>
                <p v-if="props.wireDetails.accountType" class="text-xs text-theme-body mt-0.5">{{
                    props.wireDetails.accountType }}</p>
            </div>
            <CopyActionButton :value="props.wireDetails.accountName" copy-key="wireBeneficiary"
                :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
        </div>

        <div :class="contentGridClass">
            <div
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Bank</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5">{{ props.wireDetails.bankName }}</p>
                </div>
                <CopyActionButton :value="props.wireDetails.bankName" copy-key="wireBankR" :copied-key="props.copiedKey"
                    :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3"
                :class="{ 'opacity-50': !props.zelleReference }">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Reference</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 tracking-wider">{{
                        props.zelleReference || 'Select invoices above' }}</p>
                </div>
                <CopyActionButton v-if="props.zelleReference" :value="props.zelleReference" copy-key="wireRefAmtR"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div v-if="isSidebar"
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3"
                :class="{ 'opacity-50': !props.zelleReference }">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Amount</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 amount-number">{{ props.zelleReference ?
                        `$${props.currencyFormat(props.totalAmount)}` : 'Select invoices above' }}</p>
                </div>
                <CopyActionButton v-if="props.zelleReference" :value="`$${props.currencyFormat(props.totalAmount)}`"
                    copy-key="wireAmtR" :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>
        </div>

        <div :class="contentGridClass">
            <div class="wire-info-card routing-card">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">ABA Routing /
                        Transit</p>
                    <p class="text-theme-secondary text-sm font-medium mt-0.5 tracking-widest amount-number">{{
                        chunkDigits(props.wireDetails.routing) }}</p>
                    <p class="text-xs text-theme-body-light mt-0.5">{{ digitCount(props.wireDetails.routing) }}
                        digits</p>
                </div>
                <CopyActionButton :value="props.wireDetails.routing" copy-key="Routing NumberR"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div class="wire-info-card account-card">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">Account Number</p>
                    <p class="text-theme-secondary text-sm font-medium mt-0.5 tracking-widest amount-number">{{
                        chunkDigits(props.wireDetails.account) }}</p>
                    <p class="text-xs text-theme-body-light mt-0.5">{{ digitCount(props.wireDetails.account) }}
                        digits</p>
                </div>
                <CopyActionButton :value="props.wireDetails.account" copy-key="Account NumberR"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>
        </div>

        <div :class="contentGridClass">
            <div v-if="isSidebar && props.wireDetails.swiftCode"
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
                <div>
                    <p class="text-tiny uppercase tracking-wide text-theme-body-light font-semibold">SWIFT Code</p>
                    <p class="text-theme-secondary text-sm font-medium mt-0.5 tracking-widest">{{
                        props.wireDetails.swiftCode }}</p>
                </div>
                <CopyActionButton :value="props.wireDetails.swiftCode" copy-key="wireSwift"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div v-if="props.wireDetails.bankAddress"
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
                <div>
                    <p class="text-tiny uppercase tracking-wide font-semibold" :class="addressLabelClass">
                        {{ isSidebar ? 'Bank Branch Address' : 'Bank Address' }}</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 whitespace-pre-line">{{
                        props.wireDetails.bankAddress }}</p>
                </div>
                <CopyActionButton :value="props.wireDetails.bankAddress" copy-key="wireBranchAddress"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>

            <div v-if="props.wireDetails.businessAddress"
                class="rounded-lg border border-theme-border border-opacity-40 bg-white px-3 py-2.5 flex items-start justify-between gap-3">
                <div>
                    <p class="text-tiny uppercase tracking-wide font-semibold" :class="addressLabelClass">Business
                        Address</p>
                    <p class="text-theme-secondary text-sm font-normal mt-0.5 whitespace-pre-line">{{
                        props.wireDetails.businessAddress }}</p>
                </div>
                <CopyActionButton :value="props.wireDetails.businessAddress" copy-key="wireBusinessAddress"
                    :copied-key="props.copiedKey" :copy-to-clipboard="props.copyToClipboard" />
            </div>
        </div>

        <!-- <div
            class="rounded-lg border border-theme-border border-opacity-40 bg-theme-bg-shade bg-opacity-10 px-3 py-2.5 text-xs text-theme-body leading-relaxed">
            <span class="font-semibold text-theme-secondary">Already sent it?</span>
            Tap below to ping our AR team — we'll match it to your order.
        </div> -->

        <div class="flex items-center gap-2 flex-wrap"
            :class="isSidebar ? 'pt-3 mt-1 border-t border-theme-border border-opacity-40' : 'pt-0.5'">
            <!-- <button type="button"
                class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-theme-secondary text-white text-xs font-semibold hover:bg-opacity-95 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                </svg>
                I sent the payment
            </button> -->
            <button type="button" @click="copyInstructions"
                class="text-theme-secondary inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-theme-border border-opacity-60 bg-white text-xs font-semibold hover:bg-theme-bg-shade hover:bg-opacity-20 transition-colors"
            >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
                {{ instructionsCopied ? 'Copied!' : 'Copy instructions' }}
            </button>
        </div>
    </div>
</template>

<style scoped>
.wire-info-card {
    border-width: 1px 1px 1px 3px;
    border-style: solid;
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    grid-column: auto;
    min-width: 0;
}

.routing-card {
    background: linear-gradient(to right, rgb(240, 247, 255) 0%, rgb(255, 255, 255) 64px);
    border-color: rgb(243, 239, 232) rgb(243, 239, 232) rgb(243, 239, 232) rgb(57, 95, 214);
}

.account-card {
    background: linear-gradient(to right, rgb(237, 250, 244) 0%, rgb(255, 255, 255) 64px);
    border-color: rgb(243, 239, 232) rgb(243, 239, 232) rgb(243, 239, 232) rgb(26, 170, 114);
}
</style>
